import {
    faChevronLeft,
    faChevronRight,
    faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GuidanceCallout } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import ProjectHeader from "../../../components/ProjectHeader";
import AUFMASS_STATUS from "../../../constants/aufmass-status";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { useSaveAufmassDetailsMutation } from "../../../store/services/aufmass";
import {
    applySaveSuccess,
    loadAufmassDataV2,
    prepareAufmassV2DataForSubmission,
    setAufmassError,
    setAufmassLoading,
    setShowOnlyInvalidPositions,
    updateFormFieldV2,
} from "../../../store/slices/aufmassV2Slice";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { showToast } from "../../../utils/method";
import "./add-aufmass.scss";
import AufmassFormV2 from "./components/AufmassFormV2";
import InvoiceHeaderV2 from "./components/InvoiceHeaderV2";
import PositionsContainerV2 from "./components/PositionsContainerV2";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

function AddAufmassV2({ mode = "add" }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projectId: projectIdParam, aufmassId } = useParams();
    const [searchParams] = useSearchParams();
    const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);

    // Get invoiceId from query params for add mode, or from Redux state for edit/view modes
    const invoiceIdFromParams = searchParams.get("invoiceId");

    const [validationErrors, setValidationErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const projectDetails = useSelector(selectProjectDetails);
    const [saveAufmassDetails] = useSaveAufmassDetailsMutation();

    const {
        formState: formData,
        addedGroups,
        loading,
        positions,
    } = useSelector((state) => state.aufmassV2);

    const readonly = useSelector((state) => state.aufmassV2.formState.readonly);
    // check (ACCESS_RIGHT_AREAS.INVOICE) ignored project grants.
    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canManageAufmass = can(PROJECT_AREA.INVOICE, PROJECT_ACTION.MANAGE_AUFMASS);
    const canManageSupplierInfo = can(PROJECT_AREA.INVOICE, PROJECT_ACTION.MANAGE_SUPPLIER_INFO);

    // Get the projectId either from params or from Redux store
    const { id: projectIdFromStore } = projectDetails || {};
    const projectId = projectIdParam || projectIdFromStore;

    // Determine the invoiceId based on mode
    const invoiceId = useMemo(() => {
        if (mode === "add") {
            return invoiceIdFromParams;
        }
        return formData.invoiceId || invoiceIdFromParams;
    }, [mode, invoiceIdFromParams, formData.invoiceId]);

    // Load aufmass data based on mode
    useEffect(() => {
        if (mode === "add" && invoiceId) {
            // Load data for new aufmass based on invoice
            dispatch(loadAufmassDataV2(invoiceId));
        } else if (["edit", "view"].includes(mode) && aufmassId) {
            // Load existing aufmass data
            setIsLoading(true);
            dispatch(loadAufmassDataV2(aufmassId)).finally(() =>
                setIsLoading(false),
            );
        }
    }, [dispatch, mode, invoiceId, aufmassId]);

    // Validation functions
    const validateAufmassData = useCallback(
        (formData) => {
            const errors = {};

            // Check required fields
            if (!formData.name || formData.name.trim() === "") {
                errors.name = t("aufmass.error.name-required");
            }

            if (!formData.number || formData.number.trim() === "") {
                errors.number = t("aufmass.error.number-required");
            }

            if (!formData.date) {
                errors.date = t("aufmass.error.date-required");
            }

            // Set validation errors
            setValidationErrors(errors);

            // Return true if no errors
            return Object.keys(errors).length === 0;
        },
        [t],
    );

    // Silent validation for button state - doesn't show toast messages
    const validateAufmassHasGroupsSilent = useCallback(() => {
        // V2 data structure - positions is an object where keys are position IDs
        const positionEntries = Object.entries(positions || {});

        // Filter positions that need measurements (invoice quantity > current aufmass quantity)
        const positionsThatNeedMeasurements = positionEntries.filter(
            ([positionId, positionData]) => {
                const invoiceQuantity = positionData.invoice?.quantity || 0;
                const aufmassQuantity = positionData.aufmass?.quantity || 0;

                // Only check positions where invoice quantity is greater than current aufmass quantity
                return invoiceQuantity > aufmassQuantity;
            },
        );

        // Among positions that need measurements, check which ones are missing measurement lines
        const missingPositions = positionsThatNeedMeasurements.filter(
            ([positionId, positionData]) => {
                const positionLines = addedGroups[positionId] || [];
                return positionLines?.length === 0;
            },
        );

        // Return false if there are missing positions, but don't show toast
        return missingPositions.length === 0;
    }, [positions, addedGroups]); // Save aufmass data

    const saveAufmassData = useCallback(
        (status) => {
            // Update status in form data
            const updatedFormData = { ...formData, status };
            dispatch(updateFormFieldV2({ name: "status", value: status }));

            // Validate required fields
            if (!validateAufmassData(updatedFormData)) {
                // Show validation error toast
                showToast(t("aufmass.validation-errors"), { type: "error" });

                // Scroll to AufmassForm which contains the fields
                const formElement = document.querySelector(
                    ".aufmass-form-container",
                );
                if (formElement) {
                    formElement.scrollIntoView({ behavior: "smooth" });
                }

                return;
            }

            // Prepare data for submission
            const submitData = prepareAufmassV2DataForSubmission(
                updatedFormData,
                addedGroups,
                invoiceId,
            );

            // Save data
            dispatch(setAufmassLoading(true));
            saveAufmassDetails({ projectId, invoiceId, body: submitData })
                .unwrap()
                .then(() => {
                    dispatch(applySaveSuccess());
                    showToast(t("aufmass.draft-save-success"), { type: "success" });
                })
                .catch((err) => {
                    dispatch(setAufmassError(err?.data?.message || err?.message));
                    showToast(t("aufmass.save-error"), { type: "error" });
                });
        },
        [
            dispatch,
            projectId,
            saveAufmassDetails,
            formData,
            validateAufmassData,
            addedGroups,
            invoiceId,
            t,
        ],
    );

    // Helper function to get invalid positions
    const getInvalidPositions = useCallback(() => {
        if (!positions || typeof positions !== "object") return [];

        return Object.values(positions).filter(
            (position) => position.isValidated === false,
        );
    }, [positions]);

    // Get invalid positions
    const invalidPositions = getInvalidPositions();

    // Handle show invalid positions button click
    const handleShowInvalidPositions = useCallback(() => {
        dispatch(setShowOnlyInvalidPositions(true));
    }, [dispatch]);

    // Helper function to check if any position is invalidated
    const hasInvalidatedPositions = useCallback(() => {
        if (!positions || typeof positions !== "object") return false;

        return Object.values(positions).some(
            (position) => position.isValidated === false,
        );
    }, [positions]);

    // Helper function to get disabled reason for Create Supplier Info Sheet button
    const getCreateSupplierInfoDisabledReason = useCallback(() => {
        if (!formData.id) {
            return t("aufmass.supplier-info-disabled.save-measurement-first");
        }
        if (loading) {
            return t("aufmass.supplier-info-disabled.loading");
        }
        if (hasInvalidatedPositions()) {
            return t("aufmass.positions-invalidated-supplier-info-disabled");
        }
        if (!validateAufmassData(formData)) {
            return t("aufmass.supplier-info-disabled.complete-required-fields");
        }
        if (!validateAufmassHasGroupsSilent()) {
            // Get all positions and check which ones actually need measurements
            const positionEntries = Object.entries(positions || {});

            // Filter positions that need measurements (invoice quantity > current aufmass quantity)
            const positionsThatNeedMeasurements = positionEntries.filter(
                ([positionId, positionData]) => {
                    const invoiceQuantity = positionData.invoice?.quantity || 0;
                    const aufmassQuantity = positionData.aufmass?.quantity || 0;

                    // Only check positions where invoice quantity is greater than current aufmass quantity
                    return invoiceQuantity > aufmassQuantity;
                },
            );

            // Among positions that need measurements, check which ones are missing measurement lines
            const missingPositions = positionsThatNeedMeasurements.filter(
                ([positionId, positionData]) => {
                    const positionLines = addedGroups[positionId] || [];
                    return positionLines?.length === 0;
                },
            );

            if (missingPositions.length > 0) {
                // Show first 3 positions and count the rest
                const firstThreePositions = missingPositions
                    .slice(0, 3)
                    .map(
                        ([positionId, positionData]) =>
                            `#${positionData.itemNumber}`,
                    )
                    .join(", ");

                if (missingPositions.length > 3) {
                    const remainingCount = missingPositions.length - 3;
                    const moreCountText = ` (+${remainingCount})`;
                    return t(
                        "aufmass.measurement-details-missing-first-three",
                        {
                            firstThree: firstThreePositions,
                            moreCount: moreCountText,
                        },
                    );
                } else {
                    return t(
                        "aufmass.measurement-details-missing-first-three",
                        {
                            firstThree: firstThreePositions,
                            moreCount: "",
                        },
                    );
                }
            }
            return t("aufmass.measurement-details-missing-all");
        }
        return null;
    }, [
        loading,
        hasInvalidatedPositions,
        validateAufmassData,
        formData,
        validateAufmassHasGroupsSilent,
        positions,
        addedGroups,
        t,
    ]); // Memoized filter items

    const filterItems = useMemo(() => {
        const items = [
            {
                type: "backButton",
                key: "back",
                label: t("common.back"),
            },
        ];

        // Add action buttons when not in readonly mode
        const isReadonly = mode === "view" || readonly;

        if (!isReadonly) {
            const isCreateSupplierInfoDisabled =
                !formData.id ||
                loading ||
                hasInvalidatedPositions() ||
                !validateAufmassData(formData) ||
                !validateAufmassHasGroupsSilent();

            items.push(
                {
                    displayPosition: "right",
                    type: "button",
                    key: "save",
                    labelKey: "common.save",
                    className: "btn btn-success me-2",
                    disabled: loading,
                },
                ...(canManageSupplierInfo ? [{
                    displayPosition: "right",
                    type: "button",
                    key: "create-supplier-info",
                    labelKey: "aufmass.create-supplier-info-sheet",
                    className: `btn btn-primary ${isCreateSupplierInfoDisabled ? "disabled" : ""}`,
                    disabled: isCreateSupplierInfoDisabled,
                    disabledReason: isCreateSupplierInfoDisabled
                        ? getCreateSupplierInfoDisabledReason()
                        : null,
                }] : []),
            );
        }

        return items;
    }, [
        t,
        mode,
        readonly,
        loading,
        hasInvalidatedPositions,
        formData,
        validateAufmassData,
        validateAufmassHasGroupsSilent,
        getCreateSupplierInfoDisabledReason,
        canManageSupplierInfo,
    ]);

    // Memoized filter action handler
    const onFilterAction = useCallback(
        (index, key) => {
            switch (key) {
                case "back":
                    navigate(`/projects/${projectId}/invoice`);
                    break;
                case "save":
                    saveAufmassData(AUFMASS_STATUS.DRAFT);
                    break;
                case "create-supplier-info":
                    // Handle create Supplier Info Sheet action - ensure aufmass is saved first

                    // Check if aufmass is saved
                    if (!formData.id || loading) {
                        showToast(
                            t("aufmass.error-save-before-supplier-info"),
                            {
                                type: "warning",
                            },
                        );
                        return;
                    }

                    // Navigate to supplier info sheet creation using the invoice route
                    navigate(
                        `/projects/${projectId}/invoice/supplier-info/${invoiceId}`,
                    );
                    break;
                default:
                    break;
            }
        },
        [
            navigate,
            projectId,
            saveAufmassData,
            formData.id,
            loading,
            t,
            invoiceId,
        ],
    );

    // Loading state for edit/view modes
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                        {t("common.loading")}
                    </span>
                </div>
            </div>
        );
    }

    // Aufmass is gated like supplier-info: without invoice.manageAufmass a member
    // is blocked entirely (entry buttons are also gated on it). This page-level
    // guard backstops direct-URL navigation to the editor/view routes.
    if (!permissionLoading && !canManageAufmass) {
        return (
            <div className="aufmass-add-page">
                <ProjectHeader
                    projectName={projectDetails?.name || ""}
                    tabTitle={t("aufmass.title")}
                />
                <NoPermissionAlert />
            </div>
        );
    }

    return (
        <div className="aufmass-add-page">
            <ProjectHeader
                projectName={projectDetails?.name}
                tabTitle={t("aufmass.title")}
            />

            <div className="page-container">
                <FilterBar items={filterItems} onFilterAction={onFilterAction} />

                <div className="aufmass-content">
                    <div className="aufmass-twocol-layout">
                        {/* Left details panel */}
                        <div className={`aufmass-left-panel${leftPanelCollapsed ? " collapsed" : ""}`}>
                            {/* Slim strip - click to expand when collapsed */}
                            <div
                                className="panel-slim-tab"
                                onClick={() => setLeftPanelCollapsed(false)}
                                title={t("common.expand")}
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="panel-slim-tab-icon" />
                                <span className="panel-slim-tab-label">{t("aufmass.basic-information")}</span>
                                <FontAwesomeIcon icon={faChevronRight} className="panel-slim-tab-chevron" />
                            </div>

                            {/* Full panel content */}
                            <div className="aufmass-left-panel-header">
                                <div className="panel-header-content">
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    {t("aufmass.basic-information")}
                                </div>
                                <button
                                    className="panel-collapse-btn"
                                    onClick={() => setLeftPanelCollapsed(true)}
                                    title={t("common.collapse")}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                            </div>
                            <div className="aufmass-left-body">
                                <InvoiceHeaderV2 />
                                <AufmassFormV2 validationErrors={validationErrors} />
                            </div>
                        </div>

                        {/* Right positions area */}
                        <div className="aufmass-right-area">
                            {/* Guidance: invalid positions */}
                            {invalidPositions.length > 0 && (
                                <div className="aufmass-info-strip">
                                    <GuidanceCallout
                                        variant="warning"
                                        title={t("aufmass.guidance.invalid-positions.title", { count: invalidPositions.length })}
                                        steps={[
                                            t("aufmass.guidance.invalid-positions.step-1"),
                                            t("aufmass.guidance.invalid-positions.step-2"),
                                            t("aufmass.guidance.invalid-positions.step-3"),
                                        ]}
                                        actions={[{
                                            label: t("aufmass.invalid-position-alert.show-invalid-positions"),
                                            onClick: handleShowInvalidPositions,
                                            variant: "warning",
                                        }]}
                                    />
                                </div>
                            )}

                            {/* Positions center stage */}
                            <div className="aufmass-positions-wrapper">
                                <PositionsContainerV2 />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddAufmassV2;
